import { api } from './api';
import {
  Project, Task, ProjectFile, Timeline, Comment,
  CreateProjectData, UpdateProjectData,
  CreateTaskData, UpdateTaskData,
  CreateTimelineData, CreateCommentData
} from '../store/projectsStore';
import type { ApiResponse } from '../utils/types';
import { toast } from 'sonner';

const API_ENDPOINTS = {
  projects: '/projects',
  project: (id: string) => `/projects/${id}`,
  fromContract: (contractId: string) => `/projects/from-contract/${contractId}`,
  tasks: (projectId: string) => `/projects/${projectId}/tasks`,
  task: (id: string) => `/tasks/${id}`,
  files: (projectId: string) => `/projects/${projectId}/files`,
  file: (id: string) => `/project-files/${id}`,
  timelines: (projectId: string) => `/projects/${projectId}/timelines`,
  timeline: (id: string) => `/timelines/${id}`,
  comments: (projectId: string) => `/projects/${projectId}/comments`,
  comment: (id: string) => `/comments/${id}`,
};

function isErrorWithResponse(error: unknown): error is { response?: { status?: number; data?: { message?: string } } } {
  return typeof error === 'object' && error !== null && 'response' in error;
}

export class ProjectsService {
  // Projects
  async getProjects(): Promise<Project[]> {
    try {
      const response = await api.get<ApiResponse<Project[]>>(API_ENDPOINTS.projects);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch projects');
      return response.data.data.map(this.transformProject);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      throw new Error('Failed to fetch projects. Please try again.');
    }
  }

  async createProject(data: CreateProjectData): Promise<Project> {
    try {
      const response = await api.post<ApiResponse<Project>>(API_ENDPOINTS.projects, data);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to create project');
      toast.success('Project created successfully');
      return this.transformProject(response.data.data);
    } catch (error) {
      console.error('Failed to create project:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to create project. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async updateProject(data: UpdateProjectData): Promise<Project> {
    try {
      const { id, ...updateData } = data;
      const response = await api.patch<ApiResponse<Project>>(API_ENDPOINTS.project(id), updateData);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to update project');
      toast.success('Project updated successfully');
      return this.transformProject(response.data.data);
    } catch (error) {
      console.error('Failed to update project:', error);
      let message = 'Failed to update project. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Project not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  async deleteProject(id: string): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.project(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete project');
      toast.success('Project deleted successfully');
    } catch (error) {
      console.error('Failed to delete project:', error);
      let message = 'Failed to delete project. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Project not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  /**
   * Create a project from an existing contract.
   * Contract deliverables are automatically converted to project tasks.
   */
  async createProjectFromContract(contractId: string): Promise<{ project: Project; tasksCreated: number }> {
    try {
      const response = await api.post<ApiResponse<Project> & { tasksCreated: number }>(
        API_ENDPOINTS.fromContract(contractId)
      );
      if (!response.data.success) throw new Error(response.data.message || 'Failed to create project from contract');
      toast.success(response.data.message || 'Project created from contract');
      return {
        project: this.transformProject(response.data.data),
        tasksCreated: response.data.tasksCreated || 0,
      };
    } catch (error) {
      console.error('Failed to create project from contract:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to create project from contract. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Tasks
  async getTasks(projectId: string): Promise<Task[]> {
    try {
      const response = await api.get<ApiResponse<Task[]>>(API_ENDPOINTS.tasks(projectId));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch tasks');
      return response.data.data.map(this.transformTask);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      throw new Error('Failed to fetch tasks. Please try again.');
    }
  }

  async createTask(data: CreateTaskData): Promise<Task> {
    try {
      const { projectId, ...rest } = data;
      // Backend gets projectId from URL param, so don't send it in body
      // ValidationPipe with forbidNonWhitelisted: true will reject any non-whitelisted properties
      // Explicitly construct the body to ensure projectId is never included
      const requestBody: Omit<CreateTaskData, 'projectId'> = {
        title: rest.title,
        ...(rest.description && { description: rest.description }),
        ...(rest.assignedTo && { assignedTo: rest.assignedTo }),
        ...(rest.dueDate && { dueDate: rest.dueDate }),
        ...(rest.order !== undefined && { order: rest.order }),
      };
      const response = await api.post<ApiResponse<Task>>(API_ENDPOINTS.tasks(projectId), requestBody);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to create task');
      toast.success('Task created successfully');
      return this.transformTask(response.data.data);
    } catch (error) {
      console.error('Failed to create task:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to create task. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async updateTask(data: UpdateTaskData): Promise<Task> {
    try {
      const { id, ...updateData } = data;

      // Remove order if not provided to avoid validation errors
      if (updateData.order === undefined || updateData.order === null) {
        delete updateData.order;
      }

      // Remove attachedBy and attachedAt from attachments if present (backend sets these)
      if (updateData.attachments) {
        updateData.attachments = updateData.attachments.map((att: any) => {
          const { attachedBy, attachedAt, ...cleanAtt } = att;
          return cleanAtt;
        });
      }

      // Filter out undefined values and any fields that shouldn't be in UpdateTaskDto
      // This prevents validation errors from the backend
      const cleanUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      );
      const response = await api.patch<ApiResponse<Task>>(API_ENDPOINTS.task(id), cleanUpdateData);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to update task');
      toast.success('Task updated successfully');
      return this.transformTask(response.data.data);
    } catch (error) {
      console.error('Failed to update task:', error);
      let message = 'Failed to update task. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Task not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  async deleteTask(id: string): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.task(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete task');
      toast.success('Task deleted successfully');
    } catch (error) {
      console.error('Failed to delete task:', error);
      let message = 'Failed to delete task. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Task not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  // Files
  async getFiles(projectId: string): Promise<ProjectFile[]> {
    try {
      const response = await api.get<ApiResponse<ProjectFile[]>>(API_ENDPOINTS.files(projectId));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch files');
      return response.data.data.map(this.transformFile);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      throw new Error('Failed to fetch files. Please try again.');
    }
  }

  async uploadFile(projectId: string, file: File): Promise<ProjectFile> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post<ApiResponse<ProjectFile>>(
        API_ENDPOINTS.files(projectId),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (!response.data.success) throw new Error(response.data.message || 'Failed to upload file');
      toast.success('File uploaded successfully');
      return this.transformFile(response.data.data);
    } catch (error) {
      console.error('Failed to upload file:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to upload file. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async deleteFile(id: string): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.file(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete file');
      toast.success('File deleted successfully');
    } catch (error) {
      console.error('Failed to delete file:', error);
      let message = 'Failed to delete file. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'File not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  // Timelines
  async getTimelines(projectId: string): Promise<Timeline[]> {
    try {
      const response = await api.get<ApiResponse<Timeline[]>>(API_ENDPOINTS.timelines(projectId));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch timelines');
      return response.data.data.map(this.transformTimeline);
    } catch (error) {
      console.error('Failed to fetch timelines:', error);
      throw new Error('Failed to fetch timelines. Please try again.');
    }
  }

  async createTimeline(data: CreateTimelineData): Promise<Timeline> {
    try {
      const { projectId, ...timelineData } = data;
      const response = await api.post<ApiResponse<Timeline>>(
        API_ENDPOINTS.timelines(projectId),
        timelineData
      );
      if (!response.data.success) throw new Error(response.data.message || 'Failed to create timeline');
      toast.success('Timeline milestone added successfully');
      return this.transformTimeline(response.data.data);
    } catch (error) {
      console.error('Failed to create timeline:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to create timeline. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async updateTimeline(id: string, data: Partial<CreateTimelineData>): Promise<Timeline> {
    try {
      const response = await api.patch<ApiResponse<Timeline>>(API_ENDPOINTS.timeline(id), data);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to update timeline');
      toast.success('Timeline updated successfully');
      return this.transformTimeline(response.data.data);
    } catch (error) {
      console.error('Failed to update timeline:', error);
      let message = 'Failed to update timeline. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Timeline not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  async deleteTimeline(id: string): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.timeline(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete timeline');
      toast.success('Timeline milestone deleted successfully');
    } catch (error) {
      console.error('Failed to delete timeline:', error);
      let message = 'Failed to delete timeline. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Timeline not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  // Comments
  async getComments(projectId: string): Promise<Comment[]> {
    try {
      const response = await api.get<ApiResponse<Comment[]>>(API_ENDPOINTS.comments(projectId));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch comments');
      return response.data.data.map(this.transformComment);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      throw new Error('Failed to fetch comments. Please try again.');
    }
  }

  async createComment(data: CreateCommentData): Promise<Comment> {
    try {
      const { projectId, ...commentData } = data;
      const response = await api.post<ApiResponse<Comment>>(
        API_ENDPOINTS.comments(projectId),
        commentData
      );
      if (!response.data.success) throw new Error(response.data.message || 'Failed to create comment');
      return this.transformComment(response.data.data);
    } catch (error) {
      console.error('Failed to create comment:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to create comment. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  async updateComment(id: string, text: string): Promise<Comment> {
    try {
      const response = await api.patch<ApiResponse<Comment>>(API_ENDPOINTS.comment(id), { text });
      if (!response.data.success) throw new Error(response.data.message || 'Failed to update comment');
      toast.success('Comment updated successfully');
      return this.transformComment(response.data.data);
    } catch (error) {
      console.error('Failed to update comment:', error);
      let message = 'Failed to update comment. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Comment not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  async deleteComment(id: string): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.comment(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete comment');
      toast.success('Comment deleted successfully');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      let message = 'Failed to delete comment. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Comment not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(message);
      throw new Error(message);
    }
  }

  // Transform helpers
  private transformProject(project: any): Project {
    return {
      id: project.id || project._id,
      title: project.title || '',
      description: project.description,
      status: project.status || 'draft',
      priority: project.priority || 'medium',
      progress: project.progress || 0,
      deadline: project.deadline,
      tags: project.tags || [],
      teamId: project.teamId || '',
      teamMembers: project.teamMembers || [],
      // Contract/Partnership references
      contractId: project.contractId,
      partnershipId: project.partnershipId,
      brand: project.brand,
      createdBy: project.createdBy || '',
      archivedAt: project.archivedAt,
      archivedBy: project.archivedBy,
      createdAt: project.createdAt || new Date().toISOString(),
      updatedAt: project.updatedAt || new Date().toISOString(),
    };
  }

  private transformTask(task: any): Task {
    return {
      id: task.id || task._id,
      projectId: task.projectId || '',
      title: task.title || '',
      description: task.description,
      status: task.status || 'todo',
      priority: task.priority || 'medium',
      assignedTo: task.assignedTo,
      assignedBy: task.assignedBy,
      assignedAt: task.assignedAt,
      dueDate: task.dueDate,
      startDate: task.startDate,
      completedAt: task.completedAt,
      completedBy: task.completedBy,
      subtasks: task.subtasks || [],
      estimatedHours: task.estimatedHours,
      timeEntries: task.timeEntries || [],
      attachments: task.attachments || [],
      dependencies: task.dependencies || [],
      labels: task.labels || [],
      customFields: task.customFields || [],
      order: task.order || 0,
      isArchived: task.isArchived || false,
      createdAt: task.createdAt || new Date().toISOString(),
      updatedAt: task.updatedAt || new Date().toISOString(),
    };
  }

  private transformFile(file: any): ProjectFile {
    return {
      id: file.id || file._id,
      projectId: file.projectId || '',
      taskId: file.taskId,
      assetId: file.assetId,
      fileName: file.fileName || '',
      fileUrl: file.fileUrl || '',
      fileType: file.fileType,
      fileSize: file.fileSize,
      mimeType: file.mimeType,
      thumbnail: file.thumbnail,
      version: file.version || 1,
      previousVersions: file.previousVersions || [],
      uploadedBy: file.uploadedBy || '',
      isPublic: file.isPublic || false,
      sharedWith: file.sharedWith || [],
      tags: file.tags || [],
      description: file.description,
      createdAt: file.createdAt || new Date().toISOString(),
      updatedAt: file.updatedAt || new Date().toISOString(),
    };
  }

  private transformTimeline(timeline: any): Timeline {
    return {
      id: timeline.id || timeline._id,
      projectId: timeline.projectId || '',
      milestone: timeline.milestone || '',
      description: timeline.description,
      startDate: timeline.startDate || new Date().toISOString(),
      endDate: timeline.endDate || new Date().toISOString(),
      progress: timeline.progress || 0,
      linkedTasks: timeline.linkedTasks || [],
      status: timeline.status || 'upcoming',
      assignedTo: timeline.assignedTo || [],
      color: timeline.color,
      completedAt: timeline.completedAt,
      completedBy: timeline.completedBy,
      createdAt: timeline.createdAt || new Date().toISOString(),
      updatedAt: timeline.updatedAt || new Date().toISOString(),
    };
  }

  private transformComment(comment: any): Comment {
    return {
      id: comment.id || comment._id,
      projectId: comment.projectId || '',
      taskId: comment.taskId,
      author: comment.author || '',
      text: comment.text || '',
      mentions: comment.mentions || [],
      attachments: comment.attachments || [],
      parentCommentId: comment.parentCommentId,
      isEdited: comment.isEdited || false,
      editedAt: comment.editedAt,
      reactions: comment.reactions || [],
      isDeleted: comment.isDeleted || false,
      createdAt: comment.createdAt || new Date().toISOString(),
      updatedAt: comment.updatedAt || new Date().toISOString(),
    };
  }
}

// Singleton instance
export const projectsService = new ProjectsService();

